import { EnvelopeIcon } from '@heroicons/react/24/outline';
import SharedDialog from '@/global/Modal/Modal';
import SharedButton from '@/global/Button/Button';
import { useResendConfirmationEmail } from '@/utils/resendConfirmationEmail';

type VerifyEmailPopupProps = {
  onClose: () => void;
  email: string;
};

function VerifyEmailPopup({ onClose, email }: VerifyEmailPopupProps) {
  const { isLoading, feedback, resend } = useResendConfirmationEmail(email);

  return (
    <SharedDialog
      isOpen
      onClose={onClose}
      title="Verify your email"
      size="xs"
      hasConfirmBtn={false}
      hasCancelBtn={false}
      className="!max-w-lg !space-y-5 !p-8"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pink/10">
          <EnvelopeIcon className="h-6 w-6 text-pink" aria-hidden />
        </div>
        <div className="space-y-3 text-sm leading-relaxed text-gray-600">
          <p>
            Your application is almost complete. To submit it, verify your email address by clicking the link
            we sent to <span className="font-semibold text-gray-900">{email}</span>.
          </p>
          <p>
            While your application is being reviewed, you&apos;ll have access to a{' '}
            <strong className="text-gray-900">FREE 7-day Advanced Trial</strong> to explore all the features
            and data of our Advanced plan.
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Didn&apos;t receive the email? Check your spam folder or resend it below.
          </p>
        </div>
      </div>

      {feedback && (
        <p
          className={
            feedback.type === 'success'
              ? 'rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800'
              : 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800'
          }
          role="status"
        >
          {feedback.message}
        </p>
      )}

      <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-2 sm:flex-row sm:justify-end">
        <SharedButton
          type="button"
          variant="secondaryOutline"
          onClick={resend}
          disabled={isLoading || !email.trim()}
          className="sm:min-w-[12rem] mr-0"
        >
          {isLoading ? 'Sending...' : 'Resend email'}
        </SharedButton>
        <SharedButton type="button" variant="gradient" onClick={onClose} className="sm:min-w-[8rem] mr-0">
          Close
        </SharedButton>
      </div>
    </SharedDialog>
  );
}

export default VerifyEmailPopup;
